import React, { forwardRef, useCallback, useEffect, useRef } from "react";
import styles from "./LiquidGlass.module.css";

const BLUR_CLASS = {
  full: styles.blurFull,
  soft: styles.blurSoft,
  none: styles.blurNone,
};

/**
 * A glass pane that behaves like a water surface: moving the pointer across it
 * pushes the water up around the cursor, with a dimple under the press, a
 * refracted rim and ripples travelling outward.
 *
 * `blur` controls how much backdrop filtering the pane pays for:
 *   "full" (14px) for the large panels, where a blurred backdrop is visible
 *   "soft" (8px)  default, for content cards
 *   "none"        for long grids - translucent glass, water hover, no blur,
 *                 because each blurred element re-reads the backdrop on every
 *                 scroll frame
 *
 * The pointer position is written to CSS custom properties inside a single
 * requestAnimationFrame, so no React render happens per frame:
 *   --mx / --my      pointer position as a percentage (drives gradients)
 *   --mx-px / --my-px  the same position in pixels (drives transforms)
 *   --press          0 at the centre, 1 at the nearest edge
 *   --tilt-x/y       -1 to 1, for parallax on children
 *
 * Children can read those variables, e.g. `transform: translate3d(
 * calc(var(--tilt-x) * -3px), calc(var(--tilt-y) * -3px), 0)`.
 */
const LiquidGlass = forwardRef(function LiquidGlass(
  { children, className = "", style, blur = "soft", wash = false, as: Tag = "div", ...rest },
  forwardedRef
) {
  const glassRef = useRef(null);
  const frameRef = useRef(0);

  // Kept in a ref so the component can be composed as `as={LiquidGlass}`
  // inside Reveal, which forwards its own in-view ref.
  const attachRef = useCallback(
    (node) => {
      glassRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef]
  );

  useEffect(
    () => () => {
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
    },
    []
  );

  const handlePointerMove = useCallback((event) => {
    const node = glassRef.current;
    if (!node || frameRef.current) return;

    const { clientX, clientY } = event;

    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = 0;
      const rect = node.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const offsetX = clientX - rect.left;
      const offsetY = clientY - rect.top;
      const x = Math.min(Math.max(offsetX / rect.width, 0), 1);
      const y = Math.min(Math.max(offsetY / rect.height, 0), 1);

      node.style.setProperty("--mx", `${(x * 100).toFixed(2)}%`);
      node.style.setProperty("--my", `${(y * 100).toFixed(2)}%`);
      node.style.setProperty("--mx-px", `${offsetX.toFixed(1)}px`);
      node.style.setProperty("--my-px", `${offsetY.toFixed(1)}px`);
      node.style.setProperty("--tilt-x", (x * 2 - 1).toFixed(3));
      node.style.setProperty("--tilt-y", (y * 2 - 1).toFixed(3));
      node.style.setProperty("--press", Math.min(1, Math.hypot(x * 2 - 1, y * 2 - 1)).toFixed(3));
    });
  }, []);

  const handlePointerLeave = useCallback(() => {
    const node = glassRef.current;
    if (!node) return;

    node.style.setProperty("--mx", "50%");
    node.style.setProperty("--my", "40%");
    node.style.setProperty("--mx-px", "50%");
    node.style.setProperty("--my-px", "40%");
    node.style.setProperty("--tilt-x", "0");
    node.style.setProperty("--tilt-y", "0");
    node.style.setProperty("--press", "0");
  }, []);

  return (
    <Tag
      ref={attachRef}
      className={[styles.glass, BLUR_CLASS[blur] || styles.blurSoft, className]
        .filter(Boolean)
        .join(" ")}
      style={style}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      onPointerCancel={handlePointerLeave}
      {...rest}
    >
      {wash && <span className={styles.wash} aria-hidden="true" />}
      <span className={styles.rise} aria-hidden="true" />
      <span className={styles.dimple} aria-hidden="true" />
      <span className={styles.rippleLayer} aria-hidden="true">
        <span className={styles.ripple} />
        <span className={styles.ripple} />
        <span className={styles.ripple} />
      </span>
      {children}
    </Tag>
  );
});

export default LiquidGlass;